import { useEffect, useState } from "react";
import api from "../../lib/api";

const Stat = ({ label, value, accent, testid }) => (
  <div className="bg-white p-5" data-testid={testid}>
    <div className={`text-3xl font-black tracking-tighter ${accent ? "text-[#0033FF]" : ""}`}>{value}</div>
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mt-1">{label}</div>
  </div>
);

const Grid = ({ title, children }) => (
  <div className="mb-8">
    <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0033FF] mb-3">{title}</div>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-zinc-200 border border-zinc-200">{children}</div>
  </div>
);

function Funnel({ steps }) {
  const top = steps[0]?.count || 1;
  return (
    <div className="border border-zinc-200 p-5" data-testid="owner-funnel">
      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0033FF] mb-4">Sign-up journey (all accounts)</div>
      <div className="space-y-3">
        {steps.map((s) => (
          <div key={s.step} className="grid grid-cols-[130px_1fr_70px] items-center gap-3 text-sm">
            <div className="text-zinc-600">{s.step}</div>
            <div className="h-6 bg-zinc-100"><div className="h-6 bg-[#0033FF] transition-[width] duration-700" style={{ width: `${Math.max(2, (s.count / top) * 100)}%` }} /></div>
            <div className="font-mono text-right">{s.count} <span className="text-zinc-400 text-xs">{Math.round((s.count / top) * 100)}%</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Signups({ weeks }) {
  const max = Math.max(1, ...weeks.map((w) => w.signups));
  return (
    <div className="border border-zinc-200 p-5" data-testid="owner-signups-chart">
      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0033FF] mb-4">Sign-ups per week</div>
      <div className="flex items-end gap-2 h-40">
        {weeks.map((w) => (
          <div key={w.week_start} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
            <div className="text-xs font-mono">{w.signups}</div>
            <div className="w-full bg-[#0033FF]" style={{ height: `${(w.signups / max) * 100}%`, minHeight: 2 }} />
            <div className="text-[10px] text-zinc-400">{w.week_start.slice(5)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export const OwnerOverview = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/owner/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="h-64 border border-zinc-200 animate-pulse" data-testid="owner-overview-loading" />;
  const a = d.accounts, r = d.revenue, x = d.activity_7d;
  return (
    <div data-testid="owner-overview">
      <Grid title="Revenue">
        <Stat label="Monthly recurring" value={`$${r.mrr.toFixed(2)}`} accent testid="owner-mrr" />
        <Stat label="Paying accounts" value={r.paying} testid="owner-paying" />
        <Stat label="Free plans given" value={a.comped} />
        <Stat label="Cancellations 30d" value={r.cancellations_30d} />
        <Stat label="Open support" value={d.support_open} testid="owner-support-open" />
        <Stat label="Paused accounts" value={a.paused} />
      </Grid>
      <Grid title="Accounts">
        <Stat label="Total" value={a.total} accent testid="owner-total-accounts" />
        <Stat label="New 7 days" value={a.signups_7d} />
        <Stat label="New 30 days" value={a.signups_30d} />
        <Stat label="Entry" value={a.by_plan.entry || 0} />
        <Stat label="Executive" value={a.by_plan.executive || 0} />
        <Stat label="Enterprise" value={a.by_plan.enterprise || 0} />
      </Grid>
      <Grid title="Engagement · last 7 days">
        <Stat label="Active leaders" value={x.active_leaders} accent testid="owner-active-leaders" />
        <Stat label="Musings" value={x.musings} />
        <Stat label="Posts created" value={x.posts} />
        <Stat label="Pulses" value={x.pulses} />
        <Stat label="Pulse shares" value={x.pulse_shares} />
        <Stat label="Vizi questions" value={x.vizi_questions} />
      </Grid>
      <div className="grid lg:grid-cols-2 gap-6">
        <Funnel steps={d.funnel} />
        <Signups weeks={d.signups_by_week} />
      </div>
      <p className="text-xs text-zinc-500 mt-4">Monday Pulse emails sent in the last 30 days: {d.pulse_emails_30d}. Revenue is estimated from active subscriptions; Stripe is the official record.</p>
    </div>
  );
};
